import PropTypes from 'prop-types';
import { LayoutGrid, List } from 'lucide-react';

/**
 * Cards or table — the switch that sits beside a list's search box.
 *
 * Eleven tabs had their own copy of this and they had drifted apart: 24px
 * buttons here, 28px there, one hover turning grey and another turning the
 * shop's colour. It is the same control doing the same job on every one of
 * them, so it is one component now.
 *
 * The selected side is a raised white tile on a sunken tray — the pressed-in
 * look a segmented control has had since it was a physical thing, and the
 * reason it needs no label saying which is on. The unselected side answers to
 * the pointer in the shop's own colour rather than going a slightly darker
 * grey, because a control that barely reacts reads as one that is switched off.
 *
 * `aria-pressed` is what carries the state to a screen reader. Colour alone
 * does not, and this control has no text for it to fall back on.
 */

const tile = (on) =>
  `flex h-7 w-7 items-center justify-center rounded-[4px] transition-all duration-300 ${
    on
      ? 'bg-white shadow-[0_1px_3px_rgb(0,0,0,0.12)] text-[var(--theme-primary)]'
      : 'text-slate-400 hover:text-[var(--theme-primary)] hover:bg-white/60'
  }`;

export const ViewToggle = ({ value, onChange, gridLabel = 'Card view', tableLabel = 'Table view' }) => (
  <div className="flex shrink-0 items-center gap-0.5 rounded-md border border-[var(--theme-tertiary)] bg-[var(--theme-secondary)] p-0.5 shadow-sm">
    <button
      type="button"
      onClick={() => onChange('grid')}
      aria-label={gridLabel}
      aria-pressed={value === 'grid'}
      title={gridLabel}
      className={tile(value === 'grid')}
    >
      <LayoutGrid size={14} aria-hidden="true" />
    </button>
    <button
      type="button"
      onClick={() => onChange('table')}
      aria-label={tableLabel}
      aria-pressed={value === 'table'}
      title={tableLabel}
      className={tile(value === 'table')}
    >
      <List size={14} aria-hidden="true" />
    </button>
  </div>
);

ViewToggle.propTypes = {
  /** 'grid' or 'table'. Tabs that named their states differently — 'card',
   *  'list' — are mapped at the call site rather than accepted here, so there
   *  is one vocabulary for this across the app. */
  value: PropTypes.oneOf(['grid', 'table']).isRequired,
  onChange: PropTypes.func.isRequired,
  gridLabel: PropTypes.string,
  tableLabel: PropTypes.string,
};
